import { unsafeCSS, html, nothing } from 'lit';
import { state, property, queryAll, query } from 'lit/decorators.js';

import { PopBaseComponent } from '../../../base/pop-base-component.js';
import { Constants as ProductCenterTableWrapperConstants } from '../../product-center-table-wrapper/product-center-table-wrapper.constants.js';
import { Constants } from './esg-table.constants.js';
import {
  type IPCT,
  CurrentSort,
  Product,
  SortDirection,
  TableRecordVariant,
} from '../../product-center-table-wrapper/product-center-table-wrapper.model.js';
import { getCurrencySymbol } from '../../../common/utils/currency.js';
import { buildShareClassDescriptor } from '../../../common/utils/buildShareClass.js';
import { zustandStateToComponent } from '../../store/product-center.store';
import { TableTypeEnum } from '../../store/product-center.store.model';
import {
  checkProductCallback,
  getReportButtonAriaLabel,
  handleCommonSortByDropdownChange,
  handleSortProductDataCells,
  handleUpdate,
  setAnalyticsAttributesForBeaconIconButton,
  renderProductCellData,
  renderProductHeader,
  renderReportButton,
  renderTableCells,
  renderTableHeader,
  getProductIdentifier,
  getProductIdentifierLabel,
} from '../../product-center-table-wrapper/product-center-table-wrapper.utils.js';
import Locale from '../../../common/utils/locale.js';

import style from '../common/css/table.scss?inline';
import {
  paginateTable,
  renderPagination,
  sortProductDataCells,
} from '../common/helper/table-helper.js';

/**
 * @tag pop-pc-esg-table-component
 * @property {ProductCenterExhibit} [componentObject] ESG Table content
 * @attribute {ProductCenterExhibit} [data-component-object] ESG Table content
 */
export class PopESGTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  @property()
  private readonly ipct: IPCT = {};

  /**
   * @internal
   */
  @property()
  private filteredProducts: Product[] = [];

  /**
   * @internal
   */
  @property()
  private sortByDropdownValue =
    ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value;

  /**
   * @internal
   */
  @property()
  selectedProducts: Product['productCode'][] = [];

  @state()
  public sortDirectionState: SortDirection = SortDirection.ASCENDING;

  @property()
  tableHeaders: NodeListOf<Element> | undefined;

  @queryAll('beacon-icon-button.report-button')
  beaconIconButtons!: NodeListOf<HTMLElement>;

  @query('#esg-pagination-table')
  esgPaginationTable!: HTMLElement;

  @query('#esg-pagination')
  esgPagination!: HTMLElement;

  private readonly currentSort?: CurrentSort;

  constructor() {
    super();
    this.enableI18n = true;
  }

  /*
   * Component Methods
   */

  firstUpdated() {
    this.beaconIconButtons.forEach((button) => {
      setAnalyticsAttributesForBeaconIconButton(button);
    });
  }

  update(changedProperties: Map<string, unknown>) {
    handleUpdate(this, changedProperties);
    super.update(changedProperties);
  }

  private sortByDropdownChangeHandler(e: CustomEvent) {
    this.sortByDropdownValue = handleCommonSortByDropdownChange(
      e,
      ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION.value,
      this.tableHeaders,
      this.requestUpdate.bind(this),
    );
  }

  private sortProductDataCells(e: CustomEvent) {
    handleSortProductDataCells(this, e);
  }

  private renderTableHead() {
    return html`
      <tr>
        ${renderProductHeader(
          {
            rowspan: 1,
            sortByDropdownValue: this.sortByDropdownValue,
            sortByCallBack: this.sortByDropdownChangeHandler,
            onSort: this.sortProductDataCells,
            tableType: TableTypeEnum.ESG,
          },
          new Locale(this.language, this.country),
        )}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.PROXY_VOTING_SUMMARY,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.CARBON_FOOTPRINT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.ESG_ENGAGEMENT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
        ${renderTableHeader({
          columnId: Constants.ESG_COLUMN_IDS.ANNUAL_IMPACT_REPORT,
          headerName: `${this.localize(Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT)}`,
          colspan: 1,
          rowspan: 2,
          parentHeader: null,
          sortable: false,
          customClass:
            ProductCenterTableWrapperConstants.CUSTOM_CLASSES.HEADER_ALIGNMENT,
        })}
      </tr>
    `;
  }

  private renderTableBody() {
    if (!this.currentSort) {
      // Apply default sorting by shareClass when initializing
      this.filteredProducts = sortProductDataCells(
        undefined,
        this.filteredProducts,
        ProductCenterTableWrapperConstants.DEFAULT_SORT_BY_DROP_DOWN_OPTION
          .value,
      );
    }
    return html`
      ${this.filteredProducts.map((product: Product) => {
        return html`
          <tr>
            ${renderProductCellData({
              index: JSON.stringify(product.productName),
              sortValue: product.productName[this.sortByDropdownValue] ?? '',
              checkboxLabel: product.productName.vehicleTypeName,
              fundName: product.productName.fundName,
              shareClass: product.productName.shareClass
                ? buildShareClassDescriptor(product.productName.shareClass)
                : '',
              currencySymbol: getCurrencySymbol(
                product.productName.baseCurrencyName,
              ),
              currencyName: product.productName.baseCurrencyName,
              productIdentifier: getProductIdentifier(product, this.ipct),
              productCode: product.productCode,
              checkProductCallBack: (event: Event) =>
                checkProductCallback(event, TableTypeEnum.ESG),
              isChecked: this.selectedProducts.includes(product.productCode),
              fundLink: product.productName.fundLink,
              showPopCheckbox: false,
              disclaimers: product.disclaimers ?? undefined,
              productIdentifierLabel: this.localize(
                getProductIdentifierLabel(product, this.ipct),
              ),
              noticeLabel: this.localize(
                ProductCenterTableWrapperConstants.TRANSLATION_KEYS.NOTICE,
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.proxyVotingSummary,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.PROXY_VOTING_SUMMARY),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.carbonFootprintSummary,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT,
                  ),
                ),
                this.localize(
                  Constants.TRANSLATION_KEYS.CARBON_FOOTPRINT_REPORT,
                ),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.esgEngagementReport,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.ESG_ENGAGEMENT_REPORT),
              ),
            })}
            ${renderTableCells({
              sortValue: undefined,
              variant: TableRecordVariant.TEXT,
              cellClass: Constants.REPORT_ROW_CLASS,
              value: renderReportButton(
                product.esg?.esgImpactAnnualReport,
                getReportButtonAriaLabel(
                  product,
                  this.localize(
                    Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT,
                  ),
                ),
                this.localize(Constants.TRANSLATION_KEYS.ANNUAL_IMPACT_REPORT),
              ),
            })}
          </tr>
        `;
      })}
    `;
  }

  private applyPagination() {
    paginateTable(this.esgPaginationTable, this.esgPagination);
  }

  renderComponent() {
    return html` <beacon-table
        defer-until="visible"
        variant="fixed"
        density="standard"
        id="${ProductCenterTableWrapperConstants.TABLE_FIELD
          .ESG}-pagination-table"
        @beacon-event-element-ready="${this.applyPagination}"
      >
        <table>
          <thead>
            ${this.renderTableHead()}
          </thead>
          <tbody>
            ${this.renderTableBody()}
          </tbody>
        </table>
      </beacon-table>
      ${this.filteredProducts.length >
      ProductCenterTableWrapperConstants.PAGINATION_TABLE.DEFAULT_PAGE_SIZE
        ? renderPagination(
            this.filteredProducts,
            ProductCenterTableWrapperConstants.TABLE_FIELD.ESG,
            this.applyPagination,
          )
        : nothing}`;
  }

  render() {
    if (!this.filteredProducts && !this.componentObject) {
      return html``;
    }
    return this.renderComponent();
  }
}

customElements.define(
  Constants.TAG_NAME,
  zustandStateToComponent(
    {
      mapState: (productCenterState) => {
        return {
          ipct: productCenterState.originalProductCenterObject.ipct,
          filteredProducts: productCenterState.getFilteredProducts(),
          selectedProducts:
            productCenterState.selectedProducts[TableTypeEnum.ESG],
        };
      },
    },
    PopESGTableComponent,
  ),
);
